/** @format */

import { Fragment, useEffect, useState } from 'react';
import {
  Paper,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Checkbox } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import styles from './ModifierForm.module.css';
import ActualCommission from './ActualCommission/ActualCommission';
import { useModificationLogic } from './hooks/useModificationLogic';
import { useGroupAPI } from 'pages/PhotoAdmin/SelectShop/hooks/useGroupAPI';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
const ModifierForm = ({
  templateForm,
  modifiers,
  changeChecker,
  setModifiers,
  buttonRef,
  inherit,
  belongsTo,
  setDisabled
}) => {
  const {
    getModificationList,
    setModification,
    setModificationAvailable,
    getModificationEnabled,
    getPrintingPrice,
    printingPrices,
    getUriId
  } = useModificationLogic();

  const { fetchHeader } = useGroupAPI();
  const { t } = useTranslation();
  const shopTier = useSelector((state) => state.nonPersistedReducers?.shopTierSliceReducer?.shopTier);

  useEffect(() => {
    if (typeof belongsTo !== 'undefined' && belongsTo) {
      fetchHeader({ id: belongsTo.id });
    }
    getPrintingPrice();
  }, []);

  const [modificationList, setModificationList] = useState([]);

  const correctPriceChecker = () => {
    if (printingPrices) {
      if (
        modifiers
          ?.filter((modifier) => modifier.enabled)
          ?.find(
            (modifier) =>
              modifier?.unitPrice?.grossAmount <
              Math.round(
                printingPrices?.find((pP) => pP.modId === getUriId(modifier.modifierUriSegment))?.price?.grossAmount /
                  (1 - shopTier?.commissionRate)
              )
          )
      ) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    }
  };

  useEffect(() => {
    setModificationList(getModificationList(modifiers));
    if (templateForm) {
      changeChecker();
    }
    correctPriceChecker();
  }, [modifiers]);

  const checkFormValidity = async () => {
    let valid = true;
    modifiers.forEach(async (modifier) => {
      const element = await document.getElementById(`price_${modifier.id}`);
      if (element !== null && !element.validity.valid) {
        valid = false;
      }
    });
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (await checkFormValidity()) {
      setModifiers(modifiers);
      return true;
    } else {
      return false;
    }
  };

  return (
    <TableContainer className={styles.tablecontainer} component={Paper}>
      <form className={styles.table} onSubmit={async (e) => await handleSubmit(e)}>
        <Table aria-label='simple table' size='small'>
          <TableHead>
            <TableRow>
              <TableCell className={styles.customCell}>{t('modifierForm.available')}</TableCell>
              <TableCell className={styles.nameCell}>{t('common.name')}</TableCell>
              <Tooltip title={t('modifierForm.bruttoPriceTooltip')}>
                <TableCell className={styles.customCell}>
                  {t('modifierForm.bruttoPrice')}
                  <InfoIcon
                    style={{
                      fontSize: '0.9rem',
                      opacity: '0.5',
                      marginLeft: '10px'
                    }}
                  />
                </TableCell>
              </Tooltip>

              {shopTier?.commissionRate > 0 && (
                <Tooltip title={t('modifierForm.bruttoIncomeTooltip')}>
                  <TableCell className={styles.customCell}>
                    {t('modifierForm.bruttoIncome')}
                    <InfoIcon
                      style={{
                        fontSize: '0.9rem',
                        opacity: '0.5',
                        marginLeft: '10px'
                      }}
                    />
                  </TableCell>
                </Tooltip>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {modificationList.map(
              (
                { id, name, priority, modifierUriSegment, compatibleGroup, deliveryTypeDTO },
                idx,
                enabled = getModificationEnabled(id, modifiers)
              ) => (
                <TableRow
                  key={`modification_${id}_${name}_${idx}`}
                  hover
                  className={priority === 1 ? styles.categoryRow : ''}>
                  {priority === 1 ? ( // if its prio 1 -> printer or digital, then price is 0 by default, else the price and availability must be both filled
                    <Fragment key={`modification_${name}_inner`}>
                      <TableCell className={styles.customCell} align='center'>
                        <Checkbox
                          disabled={inherit}
                          color='primary'
                          onChange={(e) => {
                            setModificationAvailable(
                              id,
                              modifierUriSegment,
                              e.target.checked,
                              modifiers,
                              setModifiers,
                              priority,
                              compatibleGroup
                            );
                          }}
                          value={
                            modifiers === undefined || modifiers?.length < 1
                              ? true
                              : modifiers.find((originalModification) => id === originalModification.id)?.enabled
                          }
                          checked={
                            modifiers === undefined || modifiers?.length < 1
                              ? true
                              : modifiers.find((originalModification) => id === originalModification.id)?.enabled
                          }
                        />
                      </TableCell>
                      <TableCell className={styles.nameCell} component='th' scope='row' colSpan={1}>
                        {t(`templates.productTypes.${name}`) !== `templates.productTypes.${name}`
                          ? t(`templates.productTypes.${name}`)
                          : name}
                      </TableCell>
                      <TableCell colSpan={1} className={styles.customCell} align='left'></TableCell>
                      {shopTier?.commissionRate > 0 && (
                        <TableCell colSpan={1} className={styles.customCell}></TableCell>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment key={`modification_${name}_inner`}>
                      <TableCell
                        className={
                          !modifiers.find((originalModification) => id === originalModification.id)?.enabled || inherit
                            ? styles.disabledCell
                            : styles.customCell
                        }
                        align='center'>
                        <Checkbox
                          disabled={inherit}
                          color='primary'
                          onChange={(e) => {
                            setModificationAvailable(
                              id,
                              modifierUriSegment,
                              e.target.checked,
                              modifiers,
                              setModifiers,
                              priority,
                              compatibleGroup
                            );
                          }}
                          value={
                            modifiers === undefined || modifiers?.length < 1
                              ? true
                              : modifiers.find((originalModification) => id === originalModification.id)?.enabled
                          }
                          checked={
                            modifiers === undefined || modifiers?.length < 1
                              ? true
                              : modifiers.find((originalModification) => id === originalModification.id)?.enabled
                          }
                        />
                      </TableCell>
                      <TableCell
                        className={
                          !modifiers.find((originalModification) => id === originalModification.id)?.enabled || inherit
                            ? styles.disabledNameCell
                            : styles.nameCell
                        }
                        component='th'
                        scope='row'>
                        {t(`templates.productTypes.${name}`) !== `templates.productTypes.${name}`
                          ? t(`templates.productTypes.${name}`)
                          : name}
                      </TableCell>
                      <TableCell
                        className={
                          !modifiers.find((originalModification) => id === originalModification.id)?.enabled || inherit
                            ? styles.disabledCell
                            : styles.customCell
                        }
                        align='left'>
                        <Tooltip
                          placement='right'
                          open={
                            printingPrices?.find(
                              (pP) => pP.modId === getUriId(modifierUriSegment) && pP.deliveryType === deliveryTypeDTO
                            ) &&
                            modifiers?.find((originalModification) => id === originalModification.id)?.enabled &&
                            modifiers?.find((originalModification) => id === originalModification.id)?.unitPrice
                              ?.grossAmount <
                              Math.round(
                                printingPrices?.find(
                                  (pP) =>
                                    pP.modId === getUriId(modifierUriSegment) && pP.deliveryType === deliveryTypeDTO
                                )?.price?.grossAmount /
                                  (1 - shopTier?.commissionRate)
                              )
                          }
                          title={`Minimális ár: ${
                            printingPrices?.find(
                              (pP) => pP.modId === getUriId(modifierUriSegment) && pP.deliveryType === deliveryTypeDTO
                            )?.price?.grossAmount /
                              (1 - shopTier?.commissionRate) >
                            0
                              ? Math.round(
                                  printingPrices?.find(
                                    (pP) =>
                                      pP.modId === getUriId(modifierUriSegment) && pP.deliveryType === deliveryTypeDTO
                                  )?.price?.grossAmount /
                                    (1 - shopTier?.commissionRate)
                                )
                              : 0
                          }`}>
                          <TextField
                            disabled={
                              // inputDisabled ||                              |
                              !modifiers?.find((originalModification) => id === originalModification.id)?.enabled ||
                              inherit
                            }
                            value={
                              modifiers?.find((originalModification) => id === originalModification.id)?.unitPrice
                                ?.grossAmount
                            }
                            type='text'
                            inputProps={{
                              required: enabled,
                              type: 'number',
                              // readOnly: inputDisabled,
                              inputMode: 'numeric',
                              pattern: '[0-9][0-9]*',
                              min: 0,
                              style: {
                                fontFamily: 'var(--font-secondary), Sans-serif, Arial'
                              }
                            }}
                            onChange={(e) => {
                              if (!isNaN(parseInt(e.target.value))) {
                                setModification(modifiers, id, e.target.value, setModifiers);
                                setModification({
                                  currentModifiers: modifiers,
                                  newModificationId: id,
                                  value: e.target.value,
                                  setCurrentModifiers: setModifiers,
                                  grossAmount: modifiers.find((originalModification) => id === originalModification.id)
                                    ?.unitPrice?.grossAmount,
                                  taxRate: modifiers.find((originalModification) => id === originalModification.id)
                                    ?.unitPrice?.taxRate,
                                  tax: false
                                });
                              } else {
                                setModification({
                                  currentModifiers: modifiers,
                                  newModificationId: id,
                                  value: 0,
                                  setCurrentModifiers: setModifiers,
                                  grossAmount: modifiers.find((originalModification) => id === originalModification.id)
                                    ?.unitPrice?.grossAmount,
                                  taxRate: modifiers.find((originalModification) => id === originalModification.id)
                                    ?.unitPrice?.taxRate,
                                  tax: false
                                });
                              }
                            }}
                            variant='outlined'
                            id={`price_${id}`}
                            className={styles.huf}
                            size='small'></TextField>
                        </Tooltip>
                      </TableCell>

                      {shopTier?.commissionRate > 0 && (
                        <TableCell
                          colSpan={1}
                          className={
                            !modifiers.find((originalModification) => id === originalModification.id)?.enabled ||
                            inherit
                              ? styles.disabledCell
                              : styles.customCell
                          }>
                          {modifiers.find((originalModification) => id === originalModification.id)?.unitPrice
                            ?.grossAmount !== 0 && (
                            <div
                              style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                              {modifiers.find((originalModification) => id === originalModification.id)?.enabled && (
                                <ActualCommission
                                  modifierId={id}
                                  shopTier={shopTier}
                                  total={
                                    modifiers.find((originalModification) => id === originalModification.id)?.total
                                  }
                                  netAmount={
                                    modifiers.find((originalModification) => id === originalModification.id)?.unitPrice
                                      ?.grossAmount
                                  }
                                  taxRate={
                                    modifiers.find((originalModification) => id === originalModification.id)?.unitPrice
                                      ?.taxRate
                                  }
                                  modifierDeliveryType={
                                    modifiers.find((originalModification) => id === originalModification.id)
                                      ?.deliveryTypeDTO
                                  }
                                  modifierUriSegment={modifierUriSegment}
                                />
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                    </Fragment>
                  )}
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
        <button ref={buttonRef} type='submit' hidden />
      </form>
    </TableContainer>
  );
};

export default ModifierForm;
